import React, { useEffect, useState } from "react";

import { Button, LabeledField, Modal, TextInput, ToggleSwitch } from "shared/ui";
import {
    DeliveryConditionsDto,
    DeliveryConditionsFixedPriceDto,
} from "services/menu/restModels";

import { useMenuSettingsContext } from "features/MenuSettings/context";
import { parseDecimalInput, sanitizeDecimalInput } from "shared/lib/decimalInput";

interface DeliveryConditionsDialogProps {
    open: boolean;
    onClose: () => void;
}

export const DeliveryConditionsDialog = ({
    open,
    onClose,
}: DeliveryConditionsDialogProps) => {
    const { delivery, currencyId, currencyFormatter } = useMenuSettingsContext();

    const [priceToShow, setPriceToShow] = useState<string>("");
    const [freeFromToShow, setFreeFromToShow] = useState<string>("");
    const [hasFreeDelivery, setHasFreeDelivery] = useState<boolean>(false);

    const conditions = delivery.toAddress.deliveryConditions;

    useEffect(() => {
        if (!open || conditions.type !== "FixedPrice") return;

        setPriceToShow(currencyFormatter.toShow(conditions.fixedDeliveryPrice.price, true));
        setFreeFromToShow(
            currencyFormatter.toShow(conditions.freeDeliveryMinimalOrderPrice.price, true)
        );
        setHasFreeDelivery(conditions.freeDeliveryMinimalOrderPrice.price > 0);
    }, [open, conditions, currencyFormatter]);

    const handleSubmit = () => {
        const next: DeliveryConditionsFixedPriceDto = {
            type: "FixedPrice",
            fixedDeliveryPrice: {
                currencyId,
                price: currencyFormatter.toSend(parseDecimalInput(priceToShow) ?? 0),
            },
            freeDeliveryMinimalOrderPrice: {
                currencyId,
                // Порог 0 означает «бесплатной доставки нет» — так же читает гостевое меню.
                price: hasFreeDelivery
                    ? currencyFormatter.toSend(parseDecimalInput(freeFromToShow) ?? 0)
                    : 0,
            },
        };

        delivery.setDeliveryConditions(next as DeliveryConditionsDto);
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Условия доставки"
            actions={
                <>
                    <Button variant="outlined" color="neutral" onClick={onClose}>
                        Отмена
                    </Button>
                    <Button variant="contained" onClick={handleSubmit}>
                        Сохранить
                    </Button>
                </>
            }
        >
            <LabeledField label="Стоимость доставки">
                <TextInput
                    value={priceToShow}
                    onChange={(value) => setPriceToShow(sanitizeDecimalInput(value))}
                />
            </LabeledField>

            <ToggleSwitch
                checked={hasFreeDelivery}
                label="Бесплатная доставка от суммы"
                onChange={setHasFreeDelivery}
            />

            {hasFreeDelivery && (
                <LabeledField label="Сумма заказа для бесплатной доставки">
                    <TextInput
                        value={freeFromToShow}
                        onChange={(value) =>
                            setFreeFromToShow(sanitizeDecimalInput(value))
                        }
                    />
                </LabeledField>
            )}
        </Modal>
    );
};
